import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Calendar, MapPin, Clock, Ticket, X, Music, ExternalLink } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { FunnyLoader } from "@/components/FunnyLoader";
import { TickerBar } from "@/components/TickerBar";
import { ProductsSection } from "@/components/ProductsSection";
import { MetaTags } from "@/components/MetaTags";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Extract Spotify artist ID from URL
const getSpotifyArtistId = (url) => {
  if (!url) return null;
  const match = url.match(/artist\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
};

// Extract YouTube video ID from URL
const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/shorts\/([^&\s?]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1&iv_load_policy=3&fs=1&controls=1`;
    }
  }
  return null;
};

export default function MusicPage() {
  const [config, setConfig] = useState(null);
  const [events, setEvents] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [merch, setMerch] = useState([]);
  const [musicThumbnails, setMusicThumbnails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxEvent, setLightboxEvent] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [spotifyLoaded, setSpotifyLoaded] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, eventsRes, photosRes, merchRes, thumbsRes] = await Promise.all([
        axios.get(`${API}/music/config`).catch(() => ({ data: {} })),
        axios.get(`${API}/music/events`).catch(() => ({ data: [] })),
        axios.get(`${API}/music/photos`).catch(() => ({ data: [] })),
        axios.get(`${API}/music/merch`).catch(() => ({ data: [] })),
        axios.get(`${API}/music-thumbnails`).catch(() => ({ data: [] }))
      ]);
      setConfig(configRes.data);
      setEvents(eventsRes.data.filter(e => e.active));
      setPhotos(photosRes.data.filter(p => p.active));
      setMerch(merchRes.data.filter(m => m.active));
      setMusicThumbnails(thumbsRes.data.slice(0, 4));
    } catch (error) {
      console.error("Error fetching music data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const spotifyArtistId = getSpotifyArtistId(config?.spotify_url);
  const youtubeEmbedUrl = getYoutubeEmbedUrl(config?.youtube_url);
  const bannerUrl = isMobile && config?.banner_mobile ? config.banner_mobile : config?.banner_desktop;

  // Separate upcoming event (most recent/next) from past events
  const { upcomingEvent, otherEvents, allEventsSorted } = useMemo(() => {
    if (!events.length) return { upcomingEvent: null, otherEvents: [], allEventsSorted: [] };
    
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    // Sort by date - upcoming events first (ascending), then past events (descending)
    const sorted = [...events].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      const isAUpcoming = dateA >= now;
      const isBUpcoming = dateB >= now;
      
      // Both upcoming: show nearest first (ascending)
      if (isAUpcoming && isBUpcoming) return dateA - dateB;
      // Both past: show most recent first (descending)
      if (!isAUpcoming && !isBUpcoming) return dateB - dateA;
      // Upcoming before past
      return isAUpcoming ? -1 : 1;
    });
    
    // Find the first upcoming event (date >= today)
    const upcomingIndex = sorted.findIndex(e => new Date(e.date) >= now);
    
    if (upcomingIndex >= 0) {
      return {
        upcomingEvent: sorted[upcomingIndex],
        otherEvents: sorted.filter((_, i) => i !== upcomingIndex),
        allEventsSorted: sorted
      };
    }
    
    // If all events are past, show the most recent one as "upcoming"
    return {
      upcomingEvent: sorted[0], // First one is most recent past event
      otherEvents: sorted.slice(1),
      allEventsSorted: sorted
    };
  }, [events]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-20">
          <FunnyLoader />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="music-page">
      <MetaTags 
        title="Música"
        description={config?.page_subtitle || "Escucha la música de Darkar Company"}
        image={config?.spotify_image || bannerUrl}
        url="/musica"
      />
      <Navbar />

      {/* Custom Banner - Same specs as homepage */}
      {bannerUrl ? (
        <section className="relative w-full h-[215px] md:h-[430px] mt-[80px] overflow-hidden">
          <img 
            src={bannerUrl}
            alt="Music Banner"
            className="w-full h-full object-cover"
          />
        </section>
      ) : (
        <div className="h-[80px]" />
      )}

      {/* Ticker - Same as homepage */}
      <TickerBar />

      <main className="flex-1 px-4 md:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          
          {/* Page Title - Same style as "Shows" in homepage */}
          <div className="flex justify-center mb-6">
            <h1 
              className="w-full text-center px-8 py-3 bg-black text-white text-2xl md:text-3xl font-black uppercase tracking-wide hand-drawn-subtle"
              style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
            >
              {config?.page_title || "MÚSICA"}
            </h1>
          </div>

          {/* Music Platform Icons */}
          <div className="flex justify-center items-center gap-4 md:gap-6 mb-6">
            {config?.spotify_url && (
              <a 
                href={config.spotify_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#1DB954] flex items-center justify-center hover:scale-110 transition-transform"
                title="Spotify"
                data-testid="music-platform-spotify"
              >
                <i className="fa-brands fa-spotify text-white text-2xl md:text-3xl"></i>
              </a>
            )}
            {config?.youtube_music_url && (
              <a 
                href={config.youtube_music_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#FF0000] flex items-center justify-center hover:scale-110 transition-transform"
                title="YouTube Music"
                data-testid="music-platform-youtube"
              >
                <i className="fa-brands fa-youtube text-white text-2xl md:text-3xl"></i>
              </a>
            )}
            {config?.apple_music_url && (
              <a 
                href={config.apple_music_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-[#FC3C44] to-[#8C67AC] flex items-center justify-center hover:scale-110 transition-transform"
                title="Apple Music"
                data-testid="music-platform-apple"
              >
                <i className="fa-brands fa-apple text-white text-2xl md:text-3xl"></i>
              </a>
            )}
            {config?.amazon_music_url && (
              <a 
                href={config.amazon_music_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#00A8E1] flex items-center justify-center hover:scale-110 transition-transform"
                title="Amazon Music"
                data-testid="music-platform-amazon"
              >
                <i className="fa-brands fa-amazon text-white text-2xl md:text-3xl"></i>
              </a>
            )}
          </div>

          {config?.page_subtitle && (
            <p className="text-lg text-white/70 text-center mb-10">{config.page_subtitle}</p>
          )}

          {/* Mobile Only: Featured Upcoming Event - Shows after banner on mobile */}
          {isMobile && upcomingEvent && (
            <section className="mb-8">
              <h2 
                className="w-full text-center px-6 py-3 bg-[#E91E8C] text-white text-xl font-black uppercase tracking-wide hand-drawn-subtle mb-4"
                style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
              >
                🔥 Próximo Evento
              </h2>
              <EventCard event={upcomingEvent} formatDate={formatDate} onOpenLightbox={setLightboxEvent} featured />
            </section>
          )}

          {/* Two Column Layout for Desktop */}
          <div className="flex flex-col lg:flex-row gap-8 mb-16">
            
            {/* Left Column - 65% - YouTube + Spotify + Photos */}
            <div className="w-full lg:w-[65%]">
              
              {/* YouTube Video */}
              {youtubeEmbedUrl && (
                <section className="mb-8">
                  {/* Video Player */}
                  <div 
                    className="aspect-video bg-black overflow-hidden hand-drawn-subtle"
                    style={{ borderWidth: '4px', borderColor: '#fff', borderStyle: 'solid' }}
                  >
                    <iframe
                      src={youtubeEmbedUrl}
                      title={config?.youtube_title || "Video destacado"}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                </section>
              )}

              {/* Spotify Player - Lazy loaded on click */}
              {spotifyArtistId && (
                <section className="mb-8">
                  <div 
                    className="overflow-hidden rounded-xl relative" 
                    style={{ borderWidth: '4px', borderColor: '#fff', borderStyle: 'solid', minHeight: '352px' }}
                  >
                    {spotifyLoaded ? (
                      <iframe
                        src={`https://open.spotify.com/embed/artist/${spotifyArtistId}?utm_source=generator&theme=0`}
                        width="100%"
                        height="352"
                        frameBorder="0"
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        className="block"
                        style={{ display: 'block', margin: 0, padding: 0 }}
                      />
                    ) : (
                      <button
                        onClick={() => setSpotifyLoaded(true)}
                        className="w-full h-[352px] bg-gradient-to-br from-[#1DB954] to-[#191414] flex flex-col items-center justify-center gap-4 hover:opacity-90 transition-opacity cursor-pointer"
                      >
                        <i className="fa-brands fa-spotify text-6xl text-white"></i>
                        <span className="text-white font-bold text-lg uppercase">Cargar reproductor de Spotify</span>
                        <span className="text-white/60 text-sm">Haz clic para escuchar</span>
                      </button>
                    )}
                  </div>
                </section>
              )}

              {/* Music Thumbnails - Albums/Singles */}
              {musicThumbnails.length > 0 && (
                <section className="mb-8">
                  <h2 className="text-xl font-black text-white uppercase mb-4 text-center lg:text-left">
                    <span className="px-4 py-2 bg-black text-white inline-block hand-drawn-subtle" style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}>
                      🎵 Discografía
                    </span>
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                    {musicThumbnails.map((thumb) => (
                      <a
                        key={thumb.id}
                        href={thumb.link_url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block bg-black hand-drawn-subtle overflow-hidden transition-transform hover:scale-[1.02]"
                        style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                      >
                        <div className="relative aspect-square overflow-hidden">
                          {thumb.image_url ? (
                            <img
                              src={thumb.image_url}
                              alt={thumb.title || 'Álbum'}
                              className="w-full h-full object-cover transition-transform group-hover:scale-105"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-800">
                              <Music size={32} className="text-white/20" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Music size={24} className="text-white" />
                          </div>
                        </div>
                        {thumb.title && (
                          <div className="p-2">
                            <h3 className="text-white font-bold text-xs text-center line-clamp-1 group-hover:text-[#E91E8C] transition-colors">
                              {thumb.title}
                            </h3>
                          </div>
                        )}
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {/* Photo Gallery */}
              {photos.length > 0 && (
                <section>
                  <h2 className="text-2xl font-black text-white uppercase mb-6 text-center lg:text-left">
                    <span className="px-6 py-2 bg-black text-white inline-block hand-drawn-subtle" style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}>
                      📸 Galería
                    </span>
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                    {photos.map((photo) => (
                      <div 
                        key={photo.id}
                        className="relative aspect-square cursor-pointer group overflow-hidden hand-drawn-subtle"
                        style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                        onClick={() => setLightboxPhoto(photo)}
                      >
                        <img 
                          src={photo.image_url}
                          alt={photo.caption || "Band photo"}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white text-sm font-bold uppercase">Ver</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* Right Column - 35% - Events */}
            <div className="w-full lg:w-[35%]">
              {events.length > 0 && (
                <section>
                  <h2 
                    className="w-full text-center px-6 py-3 bg-[#E91E8C] text-white text-xl md:text-2xl font-black uppercase tracking-wide hand-drawn-subtle mb-6"
                    style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                  >
                    📅 {isMobile ? 'Otros Eventos' : 'Próximos Eventos'}
                  </h2>
                  <div className="grid gap-4">
                    {/* On desktop: show all events sorted. On mobile: show other events (featured shown above) */}
                    {(isMobile ? otherEvents : allEventsSorted).map((event) => (
                      <EventCard 
                        key={event.id} 
                        event={event} 
                        formatDate={formatDate} 
                        onOpenLightbox={setLightboxEvent} 
                      />
                    ))}
                    {/* On desktop, if no events in the column, show a message */}
                    {!isMobile && allEventsSorted.length === 0 && (
                      <p className="text-white/50 text-center py-4">No hay eventos programados</p>
                    )}
                    {/* On mobile, if no other events besides the featured one */}
                    {isMobile && otherEvents.length === 0 && upcomingEvent && (
                      <p className="text-white/50 text-center py-4">No hay más eventos programados</p>
                    )}
                  </div>
                </section>
              )}
            </div>
          </div>

          {/* Shopify Products Section - Full Width Below */}
          <div className="mb-16">
            <ProductsSection 
              title="Merch Oficial" 
              subtitle="Playeras, peluches y más"
              limit={4}
              showViewAll={true}
              compact={true}
            />
          </div>

        </div>
      </main>

      {/* Photo Lightbox */}
      {lightboxPhoto && (
        <div 
          className="fixed inset-0 bg-black/95 z-[10000] flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-[#E91E8C] transition-colors"
            onClick={() => setLightboxPhoto(null)}
          >
            <X size={32} />
          </button>
          <img 
            src={lightboxPhoto.image_url}
            alt={lightboxPhoto.caption || "Band photo"}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {lightboxPhoto.caption && (
            <p className="absolute bottom-8 left-0 right-0 text-center text-white text-lg">
              {lightboxPhoto.caption}
            </p>
          )}
        </div>
      )}

      {/* Event Lightbox */}
      {lightboxEvent && (
        <div 
          className="fixed inset-0 bg-black/95 z-[10000] flex items-start md:items-center justify-center pt-16 md:pt-4 px-3 md:px-4 pb-4 overflow-y-auto"
          onClick={() => setLightboxEvent(null)}
        >
          <div 
            className="bg-gradient-to-b from-gray-900 to-black max-w-lg w-full rounded-2xl overflow-hidden hand-drawn-subtle relative my-4 md:my-8"
            style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 right-2 z-10 w-9 h-9 md:w-10 md:h-10 bg-black/70 rounded-full flex items-center justify-center text-white hover:text-[#E91E8C] hover:bg-black transition-colors"
              onClick={() => setLightboxEvent(null)}
            >
              <X size={20} className="md:w-6 md:h-6" />
            </button>

            {/* Event Image - smaller on mobile */}
            {lightboxEvent.image && (
              <img 
                src={lightboxEvent.image}
                alt={lightboxEvent.title}
                className="w-full h-36 md:h-56 object-cover"
              />
            )}

            {/* Event Content - more compact on mobile */}
            <div className="p-4 md:p-6">
              <h2 className="text-xl md:text-3xl font-black text-white uppercase mb-3 md:mb-4 pr-8 break-words">
                {lightboxEvent.title}
              </h2>

              <div className="flex flex-col gap-2 md:gap-3 text-white/90 text-sm md:text-base mb-4 md:mb-6">
                <div className="flex items-center gap-3">
                  <Calendar size={20} className="text-[#E91E8C] flex-shrink-0" />
                  <span className="font-semibold">{formatDate(lightboxEvent.date)}</span>
                </div>
                {lightboxEvent.time && (
                  <div className="flex items-center gap-3">
                    <Clock size={20} className="text-[#E91E8C] flex-shrink-0" />
                    <span>{lightboxEvent.time}</span>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-[#E91E8C] flex-shrink-0 mt-0.5" />
                  <div>
                    {lightboxEvent.location_url ? (
                      <a 
                        href={lightboxEvent.location_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white hover:text-[#E91E8C] transition-colors flex items-center gap-2 group"
                      >
                        <span className="font-semibold underline underline-offset-2 group-hover:no-underline">
                          {lightboxEvent.venue}
                        </span>
                        <ExternalLink size={14} className="opacity-70" />
                      </a>
                    ) : (
                      <span className="font-semibold">{lightboxEvent.venue}</span>
                    )}
                    <p className="text-white/60 text-sm">{lightboxEvent.city}</p>
                  </div>
                </div>
              </div>

              {/* Short Description */}
              {lightboxEvent.description && (
                <p className="text-white/70 text-xs md:text-sm mb-3 md:mb-4">{lightboxEvent.description}</p>
              )}

              {/* Detailed Info */}
              {lightboxEvent.details && (
                <div className="bg-white/5 rounded-xl p-3 md:p-4 mb-4 md:mb-6">
                  <h3 className="text-[#E91E8C] font-bold uppercase text-xs md:text-sm mb-2">📋 Detalles del Evento</h3>
                  <p className="text-white/80 text-xs md:text-sm whitespace-pre-line">{lightboxEvent.details}</p>
                </div>
              )}

              {/* Ticket Button */}
              {lightboxEvent.ticket_url && (
                <a
                  href={lightboxEvent.ticket_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-4 md:px-6 py-2.5 md:py-3 bg-[#FFE500] text-black font-black uppercase text-base md:text-lg hand-drawn-subtle hover:scale-105 transition-transform"
                  style={{ borderWidth: '2px', borderColor: '#000', borderStyle: 'solid' }}
                >
                  <Ticket size={18} className="md:w-5 md:h-5" />
                  Comprar Boletos
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  );
}

// EventCard Component
function EventCard({ event, formatDate, onOpenLightbox, featured = false }) {
  const isCancelled = event.cancelled;
  
  return (
    <div 
      className={`bg-black/50 backdrop-blur-sm p-4 hand-drawn-subtle hover:bg-black/70 transition-all cursor-pointer group ${
        featured ? 'ring-2 ring-[#FFE500] ring-offset-2 ring-offset-transparent' : ''
      } ${isCancelled ? 'opacity-80' : ''}`}
      style={{ borderWidth: '3px', borderColor: isCancelled ? '#dc2626' : '#fff', borderStyle: 'solid' }}
      onClick={() => onOpenLightbox(event)}
      data-testid={`event-card-${event.id}`}
    >
      {event.image && (
        <div className="relative mb-3">
          <img 
            src={event.image} 
            alt={event.title}
            className={`w-full h-32 object-cover rounded-lg group-hover:scale-[1.02] transition-transform ${
              isCancelled ? 'grayscale' : ''
            }`}
          />
          {isCancelled && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span 
                className="bg-red-600 text-white text-lg md:text-xl font-black px-4 py-2 -rotate-12 shadow-lg uppercase tracking-wider"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                CANCELADO
              </span>
            </div>
          )}
        </div>
      )}
      
      {/* Badge de cancelado sin imagen */}
      {isCancelled && !event.image && (
        <div className="bg-red-600 text-white text-center py-2 mb-3 rounded-lg">
          <span className="font-black uppercase tracking-wider">CANCELADO</span>
        </div>
      )}
      
      <h3 className={`text-lg font-black uppercase group-hover:text-[#E91E8C] transition-colors ${
        isCancelled ? 'text-gray-400 line-through' : 'text-white'
      }`}>
        {event.title}
      </h3>
      <div className="flex flex-col gap-2 mt-2 text-white/80 text-sm">
        <span className="flex items-center gap-2">
          <Calendar size={14} className={isCancelled ? 'text-gray-500' : 'text-[#E91E8C]'} />
          {formatDate(event.date)}
        </span>
        {event.time && (
          <span className="flex items-center gap-2">
            <Clock size={14} className={isCancelled ? 'text-gray-500' : 'text-[#E91E8C]'} />
            {event.time}
          </span>
        )}
        <span className="flex items-center gap-2">
          <MapPin size={14} className={isCancelled ? 'text-gray-500' : 'text-[#E91E8C]'} />
          {event.venue}, {event.city}
        </span>
      </div>
      {event.description && (
        <p className="text-white/60 text-xs mt-2 line-clamp-2">{event.description}</p>
      )}
      
      {/* Visual hint to click */}
      <div className="mt-4 text-center">
        {isCancelled ? (
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 rounded-full text-xs font-bold uppercase">
            Evento Cancelado
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#E91E8C]/20 text-[#E91E8C] rounded-full text-xs font-bold uppercase group-hover:bg-[#E91E8C] group-hover:text-white transition-colors">
            {event.ticket_url ? (
              <>
                <Ticket size={14} />
                Ver detalles y boletos
              </>
            ) : (
              'Ver más información'
            )}
          </span>
        )}
      </div>
    </div>
  );
}
